import classNames from "classnames";
import { Dinero } from "dinero.js";
import React, { type JSX } from "react";
import { Image } from "tsi-common-react/src/common/Image";
import { Link } from "tsi-common-react/src/common/Link";
import RatingGraphic from "tsi-common-react/src/common/RatingGraphic";
import Trans from "tsi-common-react/src/common/Trans";
import { IProduct } from "tsi-common-react/src/models/catalogue.interfaces";
import { isoMonths } from "tsi-common-react/src/models/nominals";
import { trackAddToBasketEvent } from "tsi-common-react/src/utils/analytics";
import { listFinancingPlans } from "tsi-common-react/src/utils/financing";
import format from "tsi-common-react/src/utils/format";
import { getDinero, getMonthlyPrice } from "tsi-common-react/src/utils/money";
import { c, t } from "ttag";

import { IBasketLineSuggestionProps } from "./BasketLineSuggestion.interfaces";

interface IState {}

// TODO(ssr, LEVEL 7 out of 10): Migrate this component from class-style to functional-style. Rated LOE as 7/10 due to complex pricing calculations, DOM queries for financing plans, multiple helper render methods, and basket interaction logic.
export class BasketLineSuggestionB extends React.Component<
    IBasketLineSuggestionProps,
    IState
> {
    private addToBasket() {
        this.props.addBasketLine(this.props.product.url, 1);
        trackAddToBasketEvent(this.props.product, 1);
    }

    private scrollToFinePrint(event: React.MouseEvent<HTMLElement>) {
        if (event) {
            event.preventDefault();
        }

        const finePrint = document.querySelector("#basket-lines__fine-print");
        finePrint?.scrollIntoView({ behavior: "smooth" });
    }

    private buildImage(product: IProduct) {
        // Use the product image if one exists else try the parent image
        let image = product.images.slice().shift();
        if (!image && product.parent) {
            image = product.parent.images.slice().shift();
        }
        return image ? (
            <Link
                href={product.parent?.link || product.link}
                title={t`Learn more about ${product.title}`}
            >
                <Image
                    alt={product.title}
                    src={image.original}
                    className="basket-line__graphic__image"
                />
            </Link>
        ) : null;
    }

    private buildOptions() {
        const attributes = this.props.product.attributes;
        const optionKeys = attributes.product_options?.value || [];
        if (optionKeys.length <= 0) {
            return null;
        }

        const options = optionKeys.map((key) => {
            const attr = attributes[key];
            if (!attr) {
                return null;
            }
            // TODO(i18n): Keep complete sentence together with interpolation: {t`${attr.name}: ${attr.value}`}
            return (
                <li key={key} className="basket-suggestion__details__option">
                    <span>{attr.name}</span>: <strong>{attr.value}</strong>
                </li>
            );
        });

        return (
            <div className="basket-suggestion__details__options">
                <ul>{options}</ul>
            </div>
        );
    }

    private calcPlanMonths(total: Dinero) {
        const plans = listFinancingPlans();
        const availablePlans = plans.filter((plan) => {
            return plan.threshold.lessThanOrEqual(total);
        });
        const bestPlan = availablePlans[availablePlans.length - 1];
        return bestPlan ? isoMonths.unwrap(bestPlan.length) : null;
    }

    private buildStarRating(product: IProduct) {
        const rating = product.rating;
        const cardType = "cart";
        const numReviews = 5;
        const ratingNum = (
            <RatingGraphic
                rating={rating}
                numReviews={numReviews}
                onlyReviewsNum={false}
                cardClass={cardType}
                cardSize={"small"}
            />
        );
        return ratingNum;
    }

    private buildAddToBasketButton() {
        let addToCartLabel: string = t`Add to cart`;
        let isPreorder = false;
        if (
            this.props.product.attributes &&
            this.props.product.attributes.preorder &&
            this.props.product.attributes.preorder.value
        ) {
            addToCartLabel = t`Pre-Order`;
            isPreorder = true;
        }

        const buttonClasses = classNames({
            "button": true,
            "button--secondary": isPreorder,
        });

        return (
            <button
                className={buttonClasses}
                onClick={this.addToBasket.bind(this)}
            >
                {addToCartLabel}
            </button>
        );
    }

    render() {
        const product = this.props.product;
        const rootProduct = product.parent || product;
        const onFinePrintClick = (event: React.MouseEvent<HTMLElement>) => {
            this.scrollToFinePrint(event);
        };

        let financing: JSX.Element | null = null;
        if (product.price.enable_per_month_pricing) {
            // Calculate Suggested plan based on (Cart total + Suggestion Product total) / (Highest plan that total qualifies for)
            const basketTotalPrice = getDinero(this.props.basketTotal);
            const extraProductPrice = getDinero(
                this.props.product.price.cosmetic_excl_tax || "0.00",
            );
            const computedTotalPrice = basketTotalPrice.add(extraProductPrice);
            const planMonths = this.calcPlanMonths(computedTotalPrice);
            if (planMonths) {
                const computedMonthlyPrice = getMonthlyPrice(
                    getDinero(
                        this.props.product.price.cosmetic_excl_tax || "0.00",
                    ),
                    planMonths,
                );
                // TODO(i18n): Use jt to keep sentence together with interpolated components and avoid fragmenting "or...mo. for...months"
                financing = (
                    <p>
                        <button
                            onClick={onFinePrintClick}
                            className="basket-suggestion__financing-button"
                        >
                            or{" "}
                            <span className="basket-suggestion__financing--underline">
                                {format.money(computedMonthlyPrice)}/{t`mo`}.
                            </span>
                            <sup>2</sup> {t`for`}{" "}
                            <span className="basket-suggestion__financing--underline">
                                {c("BasketLineSuggestionB")
                                    .t`${planMonths} months`}
                            </span>
                            <sup>1</sup>
                        </button>
                    </p>
                );
            }
        }

        // TODO(i18n): Wrap aria-label in t tagged template: aria-label={t`Upsell Carousel`}
        return (
            <div
                role="region"
                aria-label="Upsell Carousel"
                className="basket-suggestion basket-suggestion--version-b"
            >
                <div className="basket-suggestion__row basket-suggestion__row--version-b">
                    <h4>
                        <Trans
                            fmtString={t`Complete Your Tempur-Pedic <Superscript>®</Superscript> Sleep Experience`}
                            data={{
                                Superscript: (content) => (
                                    <sup key="1">{content}</sup>
                                ),
                            }}
                        />
                    </h4>
                </div>
                <div className="basket-suggestion__row">
                    <div className="basket-suggestion__graphic">
                        {this.buildImage(product)}
                    </div>
                    <div className="basket-suggestion__details basket-suggestion__details--version-b">
                        <h4 className="basket-suggestion__details-title">
                            <Link href={rootProduct.link}>
                                {rootProduct.title}
                            </Link>
                        </h4>
                        <div className="basket-suggestion__details__tag">
                            {t`Best Seller`}
                        </div>
                        {this.buildStarRating(product)}
                        {this.buildOptions()}
                    </div>
                    <div className="basket-suggestion__price basket-suggestion__price--version-b">
                        <p>{format.money(product.price.cosmetic_excl_tax)}</p>
                        <div className="basket-suggestion__financing">
                            {financing}
                        </div>
                        {this.buildAddToBasketButton()}
                    </div>
                </div>
            </div>
        );
    }
}
